import { Flex, Segmented, Typography } from 'antd';
import { NotificationDrawer, NotificationList } from 'entities/notification';
import { EventLevelType, Notification } from 'entities/notification/model/types';
import { startCase } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { AppDispatch } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { GridContainer } from 'shared/ui';
import { useGetNotificationsQuery, useSetLevelMutation, useDeleteNotificationMutation } from '../api';
import classes from './NotificationsPage.module.css';


const LIMIT = 10;

const NotificationsPage = () => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const userMeta = useSelector(getUserMeta);
  const [notificationDrawer, setNotificationDrawer] = useState<Notification | null>(null);
  const [offset, setOffset] = useState(0);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);

  const [filter, setFilter] = useState<EventLevelType>(userMeta.notificationLevel || EventLevelType.All);
  const { showError } = useMessage();

  const { data, isLoading, isFetching, isError, error } =
    useGetNotificationsQuery(
      {
        eventType: filter || EventLevelType.All,
        limit: LIMIT,
        offset
      },
      { refetchOnMountOrArgChange: true }
    );
  const [setLevelMutation] = useSetLevelMutation();
  const [deleteNotificationMutation] = useDeleteNotificationMutation();

  const availableFilters = [
    {
      label: startCase(EventLevelType.All.toLowerCase()),
      value: EventLevelType.All
    },
    {
      label: startCase(EventLevelType.Finance.toLocaleLowerCase()),
      value: EventLevelType.Finance
    },
    {
      label: startCase(EventLevelType.Text.toLocaleLowerCase()),
      value: EventLevelType.Text
    }
  ];

  useEffect(() => {
    const notifications = data?.response.notifications;

    if (!notifications) return;

    if (offset === 0) {
      setAllNotifications(notifications);
    } else {
      setAllNotifications(prev => [...prev, ...notifications]);
    }
  }, [data, offset]);

  const handleFilterChange = async (newFilter: EventLevelType) => {
    try {
      dispatch(setActivity(true));

      await setLevelMutation(newFilter).unwrap();

      setFilter(newFilter);
      setOffset(0);
      setAllNotifications([]);
    } catch (err) {
      console.log(err);
      showError?.(t({ id: 'common.message.error' }));
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onClose = () => {
    setNotificationDrawer(null);
  }

  const handleDeleteNotification = async (notificationId: number) => {
    try {
      dispatch(setActivity(true));
      await deleteNotificationMutation({ notificationId }).unwrap();
    } catch (error) {
      console.error('Failed to delete notification:', error);
      showError?.(t({ id: 'common.message.error' }));
    } finally {
      dispatch(setActivity(false));
    }
  };

  return (
    <>
      <NotificationDrawer
        open={!!notificationDrawer}
        onClose={onClose}
        notification={notificationDrawer}
      />
      <GridContainer>
        <Flex vertical align='center' gap={12} style={{ marginTop: 30 }}>
          <Typography.Title level={2}>{t({ id: 'components.notificatonspopup.components.notificationcontent.title' })}</Typography.Title>
          <Segmented
            size='small'
            options={availableFilters}
            onChange={(value) => handleFilterChange(value as EventLevelType)}
            className={classes.segmented}
            defaultValue={userMeta.notificationLevel}
          />
          <NotificationList 
            notifications={allNotifications}
            count={data?.response.count} 
            isLoading={isLoading && offset === 0}
            isFetching={isFetching}
            setNotificationDrawer={setNotificationDrawer}
            onDeleteNotification={handleDeleteNotification}
            onShowMore={() => setOffset(prev => prev + LIMIT)}
            limit={LIMIT}
            offset={offset}
          />
        </Flex>
      </GridContainer>
    </>
  )
}

export default NotificationsPage;